import React, {useState} from 'react';
import {Community} from '../../models/community';
import {CommunityBadgeFromCommunity} from '../community-badge';
import {CommunityBadgesModal} from '../community-badges-modal';

const BADGES_VISIBLE = 3;
const BADGE_SIZE = 28;

type BadgeVariant = 'circle' | 'rounded' | 'shield';

interface CommunityBadgesRowProps {
  badgeSize?: number;
  badgeVariant?: BadgeVariant;
  communities: Community[];
  label?: React.ReactNode;
  maxVisible?: number;
  showFirstCommunityName?: boolean;
}

export const CommunityBadgesRow: React.FC<CommunityBadgesRowProps> = ({
  badgeSize = BADGE_SIZE,
  badgeVariant = 'shield',
  communities,
  label,
  maxVisible = BADGES_VISIBLE,
  showFirstCommunityName = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const visible = communities.slice(0, maxVisible);
  const remaining = communities.length - maxVisible;

  if (communities.length === 0) return null;

  const openModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };

  const tooltipAll = 'View all communities';
  const ariaLabel = `View all ${communities.length} communit${communities.length === 1 ? 'y' : 'ies'}`;

  const avatarsRow = (
    <div className="flex items-center" style={{minHeight: badgeSize}}>
      {visible.map((community, index) => (
        <div
          key={community.id}
          className="relative group shrink-0 flex items-center justify-center rounded-full ring-2 ring-white"
          style={{
            height: badgeSize,
            marginLeft: index === 0 ? 0 : 4,
            minHeight: badgeSize,
          }}
          title={`${community.name || community.id}${
            community.memberCount > 0 ? ` · ${community.memberCount} member${community.memberCount === 1 ? '' : 's'}` : ''
          }`}>
          <span className="block rounded-full overflow-hidden flex items-center">
            <CommunityBadgeFromCommunity community={community} size={badgeSize} variant={badgeVariant} />
          </span>
          {showFirstCommunityName && index === 0 && (
            <span className="text-xs font-medium text-gray-700 truncate max-w-[6rem]" title={community.name || community.id}>
              {community.name || community.id}
            </span>
          )}
        </div>
      ))}
      {remaining > 0 && (
        <div
          className="shrink-0 rounded-full flex items-center justify-center bg-gray-200/80 text-[12px] font-medium text-gray-600 ring-2 ring-white hover:bg-gray-300/80 transition-colors leading-none select-none flex-shrink-0"
          style={{
            height: badgeSize,
            marginLeft: visible.length > 0 ? 4 : 0,
            minHeight: badgeSize,
            width: badgeSize,
          }}
          title={tooltipAll}>
          <span className="tabular-nums">+{remaining}</span>
        </div>
      )}
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex flex-col items-start gap-1.5 rounded-lg px-1 py-1 text-left transition-colors hover:bg-gray-100/80 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-1"
        title={label ? undefined : tooltipAll}
        aria-label={ariaLabel}>
        {label && <span className="text-xs font-medium text-gray-600 hover:text-gray-800">{label}</span>}
        {avatarsRow}
      </button>

      <CommunityBadgesModal communities={communities} isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
};
